import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import ReportHeader from "./ReportHeader";
import ScoreSection from "./ScoreSection";
import CardBox from "./CardBox";
import ScoreCard from "./ScoreCard";
import OpioidAdherenceCard from "./OpioidAdherence";
import PointCard from "./PointCard";
import PdfFooter from "./PdfFooter";
import styles from "./Report.module.css";
import opioidStyles from "./OpioidAdherence.module.css";
import pointCardStyles from "./PointCard.module.css";
import { IBillingCycleData } from "../../hooks/patients/useFetchBillingCyclePdf";
import { IImpactOfLivingData } from "../../hooks/patients/useImpactOfLivingReport";
import { IAffectData } from "../../hooks/patients/useFetchAffect";
import DateSection from "./DateSection";
import PlatformInfo, { RTMUsed } from "./PlatformInfo";
import useGetBillingDetails from "../../hooks/patients/useGetBillingDetails";

const getHighlightConfig = (score: number | undefined | null) => {
  if (score === undefined || score === null || isNaN(score)) {
    return { label: "N/A", color: "#CCCCCC", textColor: "#000000" };
  }

  const numericScore = Number(score);
  if (isNaN(numericScore)) {
    return { label: "N/A", color: "#CCCCCC", textColor: "#000000" };
  }

  if (numericScore < 5) {
    return { label: "LOW", color: "#D4EDDA", textColor: "#155724" };
  }
  if (numericScore < 10) {
    return { label: "MODERATE", color: "#FFF3CD", textColor: "#856404" };
  }
  return { label: "HIGH", color: "#F8D7DA", textColor: "#721C24" };
};

export interface ReportPageHandles {
  getReportElement: () => HTMLDivElement | null;
}

interface Props {
  children?: React.ReactNode;
  title?: string;
  patientId?: string;
  billingCycleId?: string;
  isReadyForDownload?: boolean;
  billingCycleData: IBillingCycleData | null;
  sideEffectQuestions?: {
    _id: string;
    billedCycle: string;
    sideEffect: string[];
    text: string;
  }[];
  customWidth?: string;
  customHeight?: string;
  billingCycleDataLoading: boolean;
  sideEffectQuestionsLoading?: boolean;
  customStyle?: React.CSSProperties;
  refetchImpact?: () => Promise<IImpactOfLivingData | null>;
  isImpactDataLoading?: boolean;
  impactOfLivingData?: IImpactOfLivingData | null;
  isAffectDataLoading?: boolean;
  affectData?: IAffectData | null;
  isSideEffectsLoading?: boolean;
  sideEffects?: string[] | null;
  isOpioidDataLoading?: boolean;
  opioidData: {
    isPatientCurrentlyPrescribed: string;
    hasPatientTakeMedication: string;
    hasPatientTakeOtherMedication: string[];
    askFirstQuestion?: string;
    askMedicationQuestion?: string;
  } | null;
  riskRatingLoading?: boolean;
  riskRatingData: {
    verificationResult: string;
  } | null;
}

const ReportPage = forwardRef<ReportPageHandles, Props>(
  (
    {
      billingCycleData,
      billingCycleDataLoading,
      customWidth = "8.5in",
      patientId = "",
      customHeight = "12.3in",
      customStyle,
      isReadyForDownload = false,
      billingCycleId,
      impactOfLivingData,
      isImpactDataLoading,
      isAffectDataLoading,
      affectData,
      sideEffects,
      isSideEffectsLoading,
      isOpioidDataLoading,
      opioidData,
      riskRatingLoading,
      riskRatingData,
    },
    ref
  ) => {
    const reportRef = useRef<HTMLDivElement>(null);
    const billingId = billingCycleData?._id;

    // hooks
    const { getBillingDetails, reportBillingDetails } = useGetBillingDetails({
      billingId: billingId,
    });

    useEffect(() => {
      getBillingDetails();
    }, [billingId]);

    const transformedOpioidData = opioidData
      ? {
          isPatientCurrentlyPrescribed:
            opioidData.isPatientCurrentlyPrescribed as "yes" | "no",
          hasPatientTakeMedication: opioidData.hasPatientTakeMedication,
          hasPatientTakeOtherMedication:
            opioidData.hasPatientTakeOtherMedication,
          askFirstQuestion: opioidData.askFirstQuestion,
          askMedicationQuestion: opioidData.askMedicationQuestion,
        }
      : undefined;

    useImperativeHandle(ref, () => ({
      getReportElement: () => reportRef.current,
    }));

    const isPdfGeneration = typeof isReadyForDownload === "boolean";
    const isVisible = !isPdfGeneration || isReadyForDownload;

    // Validate if affect data has meaningful content
    const hasAffectData =
      affectData && (affectData.depressionScore || affectData.anxietyScore);

    // Validate if opioid data has meaningful content (not an error response)
    const hasValidOpioidData =
      opioidData &&
      opioidData.hasPatientTakeOtherMedication &&
      Array.isArray(opioidData.hasPatientTakeOtherMedication);

    const affectsData = [
      {
        label: "Depression",
        positive: {
          value: affectData?.depressionScore === "high" ? 1 : 0,
          risk: affectData?.depressionScore === "high" ? "High" : "Low",
          trend: "stable" as const,
          lastUpdated: new Date().toISOString(),
        },
        negative: {
          value: affectData?.depressionScore === "high" ? 0 : 1,
          risk: affectData?.depressionScore === "high" ? "Low" : "High",
          trend: "stable" as const,
          lastUpdated: new Date().toISOString(),
        },
      },
      {
        label: "Anxiety",
        positive: {
          value: affectData?.anxietyScore === "high" ? 1 : 0,
          risk: affectData?.anxietyScore === "high" ? "High" : "Low",
          trend: "stable" as const,
          lastUpdated: new Date().toISOString(),
        },
        negative: {
          value: affectData?.anxietyScore === "high" ? 0 : 1,
          risk: affectData?.anxietyScore === "high" ? "Low" : "High",
          trend: "stable" as const,
          lastUpdated: new Date().toISOString(),
        },
      },
    ];

    return (
      <div
        ref={reportRef}
        className={`${styles.page} page-break`}
        style={{
          width: customWidth,
          height: customHeight,
          pageBreakAfter: "always",
          visibility: isVisible ? "visible" : "hidden",
          position:
            isPdfGeneration && !isReadyForDownload ? "absolute" : "static",
          left: isPdfGeneration && !isReadyForDownload ? "-9999px" : "0",
          ...(customStyle || {}),
        }}
      >
        <div className={styles.content}>
          <div className={styles.fixedSection}>
            <ReportHeader
              billingCycleData={billingCycleData}
              billingCycleDataLoading={billingCycleDataLoading}
            />
          </div>

          <div className={styles.scrollableContent}>
            <div className={styles.innerContent}>
              <DateSection
                billingCycleData={billingCycleData}
                consentDate={reportBillingDetails?.data.data.consentDate}
              />
              <RTMUsed data={reportBillingDetails} />

              <div className={styles.summarySection}>
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <PlatformInfo data={reportBillingDetails} />
                    <ScoreSection
                      patientId={patientId}
                      billingCycleId={billingCycleId || ""}
                      riskRatingData={riskRatingData}
                      riskRatingLoading={riskRatingLoading}
                    />
                  </div>
                  <CardBox
                    heading="FUNCTION LEVEL"
                    impactData={impactOfLivingData}
                    showFunctionLevelTable={true}
                    intakeFormDataloading={isImpactDataLoading}
                  />
                </div>

                <div className={styles.scoreCardsRow}>
                  {isAffectDataLoading ? (
                    <div className={pointCardStyles.loadingCard}>
                      <div className={pointCardStyles.loadingHeader}>
                        <h3>AFFECT</h3>
                      </div>
                      <div className={pointCardStyles.loadingContent}>
                        Loading...
                      </div>
                    </div>
                  ) : !hasAffectData ? (
                    <div className={pointCardStyles.loadingCard}>
                      <div className={pointCardStyles.loadingHeader}>
                        <h3>AFFECT</h3>
                      </div>
                      <div className={pointCardStyles.loadingContent}>
                        No data available
                      </div>
                    </div>
                  ) : (
                    <ScoreCard
                      title="AFFECT"
                      affectType="dual"
                      lastUpdated={new Date().toLocaleDateString()}
                      affects={affectsData}
                      onViewDetails={() => {}}
                    />
                  )}
                  {isOpioidDataLoading ? (
                    <div className={opioidStyles.loadingCard}>
                      <div className={opioidStyles.loadingHeader}>
                        OPIOID ADHERENCE
                      </div>
                      <div className={opioidStyles.loadingContent}>
                        Loading...
                      </div>
                    </div>
                  ) : (
                    <OpioidAdherenceCard data={transformedOpioidData} />
                  )}
                </div>

                <div className={styles.newCardsRow}>
                  {isOpioidDataLoading ? (
                    <div className={pointCardStyles.loadingCard}>
                      <div className={pointCardStyles.loadingHeader}>
                        <h3>ALTERNATIVE MEDICATIONS</h3>
                      </div>
                      <div className={pointCardStyles.loadingContent}>
                        Loading...
                      </div>
                    </div>
                  ) : !hasValidOpioidData ? (
                    <div className={pointCardStyles.loadingCard}>
                      <div className={pointCardStyles.loadingHeader}>
                        <h3>ALTERNATIVE MEDICATIONS</h3>
                      </div>
                      <div className={pointCardStyles.loadingContent}>
                        No data available
                      </div>
                    </div>
                  ) : (
                    <PointCard
                      title="ALTERNATIVE MEDICATIONS"
                      items={
                        opioidData.hasPatientTakeOtherMedication.length > 0
                          ? opioidData.hasPatientTakeOtherMedication.map(
                              (text, index) => ({
                                text:
                                  text && text.toLowerCase() === "n/a"
                                    ? "N/A"
                                    : text || "N/A",
                              })
                            )
                          : [{ text: "N/A" }]
                      }
                    />
                  )}

                  {isSideEffectsLoading ? (
                    <div className={pointCardStyles.loadingCard}>
                      <div className={pointCardStyles.loadingHeader}>
                        <h3>SIDE EFFECTS</h3>
                      </div>
                      <div className={pointCardStyles.loadingContent}>
                        Loading...
                      </div>
                    </div>
                  ) : (
                    <PointCard
                      title="SIDE EFFECTS"
                      useWarningIcon={true}
                      items={
                        sideEffects && sideEffects.length > 0
                          ? sideEffects.map((effect, index) => ({
                              text: effect,
                            }))
                          : []
                      }
                    />
                  )}
                </div>
              </div>
            </div>
            <PdfFooter />
          </div>
        </div>
      </div>
    );
  }
);

export default ReportPage;
